import { useLocation } from "react-router-dom";
import { useMemo } from "react";

export interface PageContext {
  route: string;
  page: string;
  guidance_hints: string[];
  suggested_actions: { label: string; route?: string; query?: string }[];
}

const PAGE_MAP: Record<string, { page: string; hints: string[]; actions: { label: string; route?: string; query?: string }[] }> = {
  "/dashboard": {
    page: "Progression",
    hints: [
      "L'utilisateur consulte sa progression et ses métriques.",
      "Guide-le vers des actions concrètes : journal du jour, repos, prochaine action.",
    ],
    actions: [
      { label: "Que faire maintenant ?", query: "Selon mon profil et ma priorité actuelle, que puis-je faire maintenant ?" },
      { label: "Besoin de repos ?", query: "Ai-je besoin de repos selon mon profil et mes données récentes ?" },
    ],
  },
  "/library": {
    page: "Bibliothèque",
    hints: [
      "L'utilisateur parcourt la bibliothèque de faits et sources.",
      "Guide-le vers la recherche, les filtres, l'ajout de sources, et la compréhension des badges de confiance.",
    ],
    actions: [
      { label: "Importer une source", route: "/library?tab=sources" },
      { label: "Faits à valider", query: "Quels faits dans la base nécessitent plus de sources pour renforcer leur confiance ?" },
    ],
  },
  "/profile": {
    page: "Profil",
    hints: [
      "L'utilisateur consulte ou modifie son profil sportif.",
      "Guide-le vers la complétion du profil : multi-sport, blessures, matériel, planning.",
    ],
    actions: [
      { label: "Compléter mon profil", route: "/onboarding" },
      { label: "Impact de mon profil", query: "Quels faits scientifiques sont les plus pertinents pour mon profil actuel ?" },
    ],
  },
  "/programs": {
    page: "Programmes",
    hints: [
      "L'utilisateur consulte les programmes d'entraînement.",
      "Guide-le vers la génération de programme adapté ou l'explication des blocs.",
    ],
    actions: [
      { label: "Quel programme ?", query: "Quel type de programme est adapté à mon profil et objectif ?" },
      { label: "Risques blessure", query: "Quels sont les risques de blessure liés à mon profil et mes objectifs ?" },
    ],
  },
  "/admin": {
    page: "Admin",
    hints: [
      "L'utilisateur est en mode administration.",
      "Guide-le vers la modération : faits à valider, sources bloquées, rapports, pipeline.",
    ],
    actions: [
      { label: "Faits en attente", query: "Combien de faits sont en attente de validation et quels sont les critères ?" },
      { label: "Sources bloquées", query: "Y a-t-il des sources avec des erreurs ou un timeout dans le pipeline ?" },
    ],
  },
  "/reasoning": {
    page: "Raisonnement",
    hints: [
      "L'utilisateur utilise le moteur de raisonnement en mode plein écran.",
    ],
    actions: [
      { label: "Mon objectif", query: "Quels faits scientifiques soutiennent mon objectif principal ?" },
      { label: "Récupération", query: "Quelles recommandations de récupération s'appliquent à mon profil ?" },
    ],
  },
  "/onboarding": {
    page: "Onboarding",
    hints: [
      "L'utilisateur configure son profil sportif pour la première fois.",
    ],
    actions: [],
  },
  "/community": {
    page: "Communauté",
    hints: ["Cette section n'est pas encore complètement disponible."],
    actions: [
      { label: "Importer des sources", route: "/library" },
      { label: "Voir mon profil", route: "/profile" },
    ],
  },
  "/nutrition": {
    page: "Nutrition",
    hints: ["Cette section n'est pas encore complètement disponible."],
    actions: [
      { label: "Faits sur la nutrition", query: "Quels faits scientifiques validés concernent la nutrition sportive ?" },
    ],
  },
  "/prevention": {
    page: "Prévention blessures",
    hints: ["Cette section n'est pas encore complètement disponible."],
    actions: [
      { label: "Mes risques", query: "Quels sont les risques de blessure liés à mon profil ?" },
    ],
  },
  "/avatar": {
    page: "Projection Avatar",
    hints: ["Cette section n'est pas encore complètement disponible."],
    actions: [
      { label: "Compléter mon profil", route: "/onboarding" },
    ],
  },
};

const DEFAULT_CONTEXT = {
  page: "Autre",
  hints: [],
  actions: [
    { label: "Que faire maintenant ?", query: "Selon mon profil, que puis-je faire maintenant ?" },
  ],
};

export function usePageContext(): PageContext {
  const location = useLocation();

  return useMemo(() => {
    const pathname = location.pathname;
    const config = PAGE_MAP[pathname] || DEFAULT_CONTEXT;
    return {
      route: pathname,
      page: config.page,
      guidance_hints: config.hints,
      suggested_actions: config.actions,
    };
  }, [location.pathname]);
}
